import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { QuotationsData } from "../../../../contexts";
import GetAllQuotations from "../../../../hooks/fetch-hooks/fetch-all/get-all-quotations";
import PharmacyQuotationsTableInstance from "./table-instance";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";

// Pharmacist Quotations -- /v2/pharmacy/quotations.
//
// Previously a v2-native page (read-only: no way to change a quotation's status).
// Rebuilt on the same legacy quotationsTable stack the ops console's own Quotations
// screen (../../ops/routes.jsx renders legacy `Quotations` directly) uses -- see
// ./table-instance.jsx for the column assembly, the registry-driven status select, and
// the exported `Cell` reuse for Delete/Edit.
//
// Two things carried forward from the v2-native build:
//   1. The Rx uploaded/missing chips -- CLIENT-SIDE, same as before: `prescription.
//      links` is eager-loaded by the endpoint but not exposed as a filter, so this
//      narrows the fetched page, not the whole set, and the total below the table
//      still reflects the unfiltered count.
//   2. The Order Id column (./columns.jsx) -- the reliable conversion signal, since
//      only ~12% of genuinely converted quotations carry CONVERTED_TO_ORDER.

const RX_CHIPS = [
    { id: "all", label: "All quotations" },
    { id: "uploaded", label: "Rx uploaded" },
    { id: "missing", label: "Rx missing" },
];

/**
 * Shared by /v2/pharmacy/quotations and /v2/sales/quotations. Every prop defaults to the
 * pharmacy configuration, so that route is unchanged. The Sales console passes its own
 * columns, its omit list, its quotationType and includeFinance -- see
 * src/v2/sales/pages/quotations/index.jsx.
 */
function PharmacyQuotationsPage({
    extraColumns,
    omitHeaders,
    quotationType = "PHARMACY_QUOTATIONS",
    includeFinance = false,
    title,
}) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [data, setData] = useState();
    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageSort, setPageSort] = useState([]);
    const [pageindex, setPageIndex] = useState(Number(searchParams.get("page")) || 1);
    const [value, setValue] = useState(searchParams.get("search") || "");
    const [debouncedSearch, setDebouncedSearch] = useState(value);
    const [rx, setRx] = useState(searchParams.get("rx") || "all");

    const getData = async () => {
        const fetchedData = await GetAllQuotations(
            {
                page: pageindex,
                size: DEFAULT_PAGE_SIZE,
                patientName: debouncedSearch,
                userName: debouncedSearch,
                // Opt-in per console: the Sales screen's Discount % column needs
                // pillup-api's finance rollup. undefined is omitted by axios, so the
                // pharmacy console sends exactly the request it sent before.
                includeFinance: includeFinance || undefined,
            },
            ""
        );
        // Same daysLeft convention legacy's own quotations.jsx uses (QuotationsTable
        // in src/components/quotationsTable/quotations.jsx) -- kept identical rather
        // than introducing a second formula for the same column.
        const rows = (fetchedData?.data?.data?.rows || []).map((row) => ({
            ...row,
            daysLeft: Math.floor((new Date(row.endDate) - new Date()) / (1000 * 60 * 60 * 24) + 2),
        }));
        setTableData(rows);
        setTotal(fetchedData?.data?.data?.count || 0);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(value);
            setPageIndex(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [value]);

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageindex, debouncedSearch]);

    useEffect(() => {
        setSearchParams({ page: pageindex, search: value, rx });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageindex, value, rx]);

    const visibleRows = React.useMemo(() => {
        if (rx === "uploaded") return tableData.filter((row) => row.prescription?.links?.length);
        if (rx === "missing") return tableData.filter((row) => !row.prescription?.links?.length);
        return tableData;
    }, [tableData, rx]);

    const maxValues = Math.ceil(total / DEFAULT_PAGE_SIZE) || 1;

    const handleDeleteRow = (id) => setTableData((prev) => prev.filter((item) => item.id !== id));

    return (
        <div className="p-7 h-full overflow-y-scroll bg-gray-50">
            <QuotationsData.Provider value={{ data, setData, quotationType, setQuotationType: () => {} }}>
                {title && <h2 className="text-2xl font-semibold mb-4">{title}</h2>}
                <div className="flex gap-4 items-center relative flex-wrap">
                    <input
                        value={value}
                        className="px-6 font-sans w-[60%] bg-slate-100 py-4"
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={`${total} records...`}
                        style={{ fontSize: "1.1rem", border: "0" }}
                    />
                    <div className="flex gap-2">
                        {RX_CHIPS.map((chip) => (
                            <button
                                key={chip.id}
                                type="button"
                                onClick={() => setRx(chip.id)}
                                className={`rounded-[10px] px-2 py-1 font-sans text-[10px] font-semibold transition-colors ${
                                    chip.id === rx ? "bg-elm-500 text-white-50" : "bg-gray-200 text-[#333]"
                                }`}
                            >
                                {chip.label}
                            </button>
                        ))}
                    </div>
                </div>
                {rx !== "all" && (
                    <p className="text-xs text-gray-500 my-2">
                        The prescription filter applies to this page of results only -- there is no
                        server-side filter for it, so the count above still reflects every quotation
                        matching the search.
                    </p>
                )}
                <PharmacyQuotationsTableInstance
                    extraColumns={extraColumns}
                    omitHeaders={omitHeaders}
                    quotationType={quotationType}
                    tableData={visibleRows}
                    pageSort={pageSort}
                    setPageSort={setPageSort}
                    handleDeleteRow={handleDeleteRow}
                    refetchOrders={getData}
                />
                <div className="btn_div">
                    <button onClick={() => setPageIndex((count) => Number(count) - 1)} disabled={pageindex <= 1} className="previous">
                        Previous
                    </button>
                    <span className="pageindex">{`Page ${pageindex} of ${maxValues}`}</span>
                    <button onClick={() => setPageIndex((count) => Number(count) + 1)} disabled={pageindex >= maxValues} className="next">
                        Next
                    </button>
                </div>
            </QuotationsData.Provider>
        </div>
    );
}

// Every prop is optional and defaults to the pharmacy behaviour, so /v2/pharmacy/quotations
// renders this with no arguments at all.
PharmacyQuotationsPage.propTypes = {
    extraColumns: PropTypes.func,
    omitHeaders: PropTypes.arrayOf(PropTypes.string),
    quotationType: PropTypes.string,
    includeFinance: PropTypes.bool,
    title: PropTypes.string,
};

export default PharmacyQuotationsPage;
